import { ConfigurationFeatureFlag, ConfigurationModel } from '@tink/config/src/tinkLinkConfig';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { shouldAutoRedirectMobileDeviceToThirdParty } from '../../../models/configuration';
import { CredentialsEntity, ThirdpartyAuthenticationPayload } from '../../../models/credentials';
import { applicationParameters } from '../../../utilities/application-parameters';
import { nestedPopup } from '../../../utilities/nested-popup';

export type NavigationStrategy = 'popup' | 'sameWindow';

type SupplementalInformation = CredentialsEntity['supplementalInformation'];

type GetRedirectUrlParams = {
  supplementalInformation: SupplementalInformation;
};

type NavigateToThirdPartyParams = {
  redirectUrl: string;
  navigationStrategy: NavigationStrategy;
  popup?: typeof nestedPopup;
  windowLocation?: Location;
};

type ShouldAutoRedirectParams = {
  isDesktop: boolean;
  configuration: ConfigurationModel;
  navigationStrategy: NavigationStrategy;
  redirectUrl: string | null;
};

export const getSafariEndFlowStrategy = (): boolean => {
  const isEmbeddedInNativeApp = Boolean(applicationParameters.get('appUri'));
  const isIframe = applicationParameters.get('isIframe');

  if (isEmbeddedInNativeApp || isIframe) {
    return false;
  }

  return Boolean(userAgent.isAppleDevice);
};

export const getNavigationStrategy = (): NavigationStrategy => {
  const isIframe = applicationParameters.get('isIframe');
  const isEmbeddedInNativeApp = Boolean(applicationParameters.get('appUri'));
  const hasAutoRedirectMobile = Boolean(applicationParameters.get('autoRedirectMobile'));

  const isEmbedded = isEmbeddedInNativeApp || isIframe;
  const isEmbeddedShouldUseSameView = isEmbeddedInNativeApp && hasAutoRedirectMobile;

  if (!isEmbedded || isEmbeddedShouldUseSameView) {
    return 'sameWindow';
  }

  return 'popup';
};

export const getRedirectUrl = ({ supplementalInformation }: GetRedirectUrlParams) => {
  const parsedSupplementalInformation: ThirdpartyAuthenticationPayload = JSON.parse(
    supplementalInformation || '[{}]'
  );

  const { desktop, ios, android } = parsedSupplementalInformation;
  const { isAppleDevice, isAndroidDevice, isDesktop } = userAgent;

  if (isAppleDevice && ios?.deepLinkUrl) {
    return ios.deepLinkUrl;
  }

  if (isAndroidDevice && android?.intent) {
    return android.intent;
  }

  if (isDesktop && desktop?.url) {
    return desktop.url;
  }

  return null;
};

export const navigateToThirdParty = ({
  redirectUrl,
  navigationStrategy,
  popup = nestedPopup,
  windowLocation = window.location,
}: NavigateToThirdPartyParams) => {
  if (navigationStrategy === 'popup') {
    popup.open(redirectUrl);
  }

  if (navigationStrategy === 'sameWindow') {
    windowLocation.replace(redirectUrl);
  }
};

export const shouldAutoRedirectSecondSCAToThirdParty = ({ featureFlags }: ConfigurationModel) => {
  // Only if both flag is set & transferID is set we should redirect
  // Since this about ONLY the second SCA, where transferID is set.
  return (
    featureFlags.includes(
      ConfigurationFeatureFlag.REDIRECT_SECOND_SCA_TO_THIRD_PARTY_AUTOMATICALLY
    ) && applicationParameters.get('transferId') !== undefined
  );
};

export const shouldAutoRedirectToThirdParty = ({
  isDesktop,
  configuration,
  navigationStrategy,
  redirectUrl,
}: ShouldAutoRedirectParams): boolean => {
  const isDeviceSuitable =
    isDesktop ||
    shouldAutoRedirectMobileDeviceToThirdParty(configuration) ||
    Boolean(applicationParameters.get('autoRedirectMobile'));
  const isNavigatingByPopup = navigationStrategy === 'popup';
  const hasRedirectUrl = Boolean(redirectUrl);

  return (
    (isDeviceSuitable || shouldAutoRedirectSecondSCAToThirdParty(configuration)) &&
    !isNavigatingByPopup &&
    hasRedirectUrl
  );
};
